import { FC, ReactNode, useMemo } from 'react';
import type { CreditPlanMiniTrackerBlock } from '@/@types/content';
import { CreditBreakdown, ItemAnalyticsModel, ItemListAnalyticsModel } from '@/@types/client-api';
import { stripHtml } from '../PDP/PDPFeaturedPublicationLayoutBlock.helpers';
import { LinkItem } from '@/@types/cms';
import withPliErrorBoundary from '../ErrorBoundary/withPliErrorBoundary';
import Block from '@/components/cms/Block';
import { AnalyticsContext } from '@/analytics/constants';
import classnames from 'classnames';
import CreditPlanMiniTrackerNonPersonalized from './MiniTracker/CreditPlanMiniTrackerNonPersonalized';
import CreditPlanMiniTrackerPersonalized from './MiniTracker/CreditPlanMiniTrackerPersonalized';
import { CreditPlanMiniTrackerBlockVariant } from './MiniTracker/constants';
import { PromoEvent } from '@/analytics/ecommerce';

type CreditPlanMiniTrackerProps = Omit<CreditPlanMiniTrackerBlock, 'variant' | 'primaryLink'> & {
  className?: string;
  breakdown: CreditBreakdown;
  jurisdictionName: string;
  jurisdictionKey?: string;
  /**
   * optional
   */
  compliancePeriodEndDate?: Date;
  isChildPlan?: boolean;
  isLoading?: boolean;
  variant?: CreditPlanMiniTrackerBlockVariant;
  primaryLink?: LinkItem;
  donutCounterText?: string;
  jurisdictionMessage?: string;
  tooltip?: ReactNode;
  onPrimaryLinkClick?: () => void;
  context?: AnalyticsContext;
  itemAnalyticsModel?: ItemAnalyticsModel;
  itemListAnalyticsModel?: ItemListAnalyticsModel;
};

export const CreditPlanMiniTrackerBlockName = 'CreditPlanMiniTrackerBlock';
export const EditorialToutWithImageBlockName = 'EditorialToutWithImageBlock';

const CreditPlanMiniTrackerBlock: FC<CreditPlanMiniTrackerProps> = ({
  className,
  header,
  body,
  primaryLink,
  showDonut,
  allOtherCreditsTooltip,
  breakdown,
  jurisdictionName,
  jurisdictionKey,
  isLoading,
  variant,
  isChildPlan,
  contentLink,
  donutCounterText,
  jurisdictionMessage,
  tooltip,
  onPrimaryLinkClick,
  creditsNeededTooltip,
  contentType,
  name,
  compliancePeriodEndDate,
  context,
  itemAnalyticsModel,
  itemListAnalyticsModel
}) => {
  const totalEarned = Math.max(breakdown?.totalEarned ?? 0, 0);
  const totalRequired = Math.max(breakdown?.totalRequired ?? 0, 0);
  const creditBreakdownCreditTypes = useMemo(
    () => breakdown?.creditBreakdownCreditTypes?.filter(a => a?.isRequired || false),
    [breakdown?.creditBreakdownCreditTypes]
  );
  const bodyText = useMemo(() => stripHtml(body), [body]);

  const promoEvent = useMemo(
    () =>
      ({
        creative_name: showDonut ? 'creditDonut' : '',
        creative_slot: contentType?.at(-1) ?? '',
        promotion_id: contentLink?.id?.toString() ?? '',
        promotion_name: name ?? '',
        context: context
      }) as PromoEvent,
    [contentLink?.id, contentType, context, name, showDonut]
  );

  return (
    <Block
      contentLinkID={contentLink?.id}
      className={classnames('container overflow-x-clip !px-0', className)}
      propertyName={CreditPlanMiniTrackerBlockName}
    >
      {variant === CreditPlanMiniTrackerBlockVariant.NonPersonalized ? (
        <CreditPlanMiniTrackerNonPersonalized
          creditsCounted={totalEarned}
          creditsRequired={totalRequired}
          creditsEarned={totalEarned}
          donutCounterText={donutCounterText}
          header={header}
          isChildPlan={isChildPlan}
          isLoading={isLoading}
          jurisdictionName={jurisdictionName}
          showDonut={showDonut}
          bodyText={bodyText}
          donutTooltip={tooltip}
          itemAnalyticsModel={itemAnalyticsModel}
          itemListAnalyticsModel={itemListAnalyticsModel}
          primaryLink={primaryLink}
          creditBreakdownCreditTypes={creditBreakdownCreditTypes}
          allOtherCreditsTooltip={allOtherCreditsTooltip}
          jurisdictionMessage={jurisdictionMessage}
          promoEvent={promoEvent}
        />
      ) : (
        <CreditPlanMiniTrackerPersonalized
          creditsCounted={totalEarned}
          creditsRequired={totalRequired}
          creditsEarned={totalEarned}
          jurisdictionKey={jurisdictionKey}
          deadline={compliancePeriodEndDate}
          primaryLink={primaryLink}
          showDonut={showDonut}
          itemAnalyticsModel={itemAnalyticsModel}
          itemListAnalyticsModel={itemListAnalyticsModel}
          creditsNeededTooltip={creditsNeededTooltip?.[0]}
          creditBreakdownCreditTypes={creditBreakdownCreditTypes}
          allOtherCreditsTooltip={allOtherCreditsTooltip}
          jurisdictionMessage={jurisdictionMessage}
          onPrimaryLinkClick={onPrimaryLinkClick}
        />
      )}
    </Block>
  );
};

const CreditPlanMiniTrackerBlockWithErrorBoundary = withPliErrorBoundary(
  CreditPlanMiniTrackerBlock,
  {
    name: CreditPlanMiniTrackerBlockName,
    sectionClassNames: '!px-0',
    buttonClassNames: '!w-fit'
  }
);

export default CreditPlanMiniTrackerBlockWithErrorBoundary;
